import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import { toastError } from '../../../shared/toastHelper';
import { MobileHoldChallengeButton, useMobileHoldChallenges } from './MobileHoldChallenges';
import '../styles/holdChallenges.css';

const STATUS_COLORS = {
  on_sale: '#22C55E',      // Green
  pre_sale: '#FF7A00',     // Orange
  artist_pre_sale: '#d36c85', // Pink
  announced: '#EAB308',    // Yellow
  confirm: '#EF4444',      // Red
  hold: '#EF4444',         // Red (for border)
  past: 'var(--od-c-969696, #969696)',         // Gray
  canceled: 'var(--od-c-969696, #969696)'      // Gray
};

const STATUS_LABELS = {
  on_sale: 'On-sale',
  pre_sale: 'Pre-sale',
  artist_pre_sale: 'Artist Pre-sale',
  announced: 'Announce',
  confirm: 'Confirm',
  hold: '',
  past: 'Past',
  canceled: 'Canceled'
};

const STATUS_ICONS = {
  on_sale: '/icons/ticket.svg',
  pre_sale: '/icons/ticket.svg',
  artist_pre_sale: '/icons/ticket.svg',
  announced: '/icons/announcement.svg',
  confirm: '/icons/calendar-check.svg',
  hold: '/icons/hold.svg',
  past: '/icons/past.svg',
  canceled: '/icons/x-square.svg'
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  // Handle various time formats
  const date = new Date(`2000-01-01 ${timeString}`);
  if (isNaN(date.getTime())) return timeString;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
};

const groupEventsByDate = (events) => {
  const grouped = {};
  events.forEach(event => {
    // Use ISO date for grouping (reliable sorting), fall back to start_date
    const dateKey = event.start_date_iso || event.start_date;
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        displayDate: event.start_date,
        events: []
      };
    }
    grouped[dateKey].events.push(event);
  });
  return grouped;
};

const EventCard = ({ event, team, prevClassification, stacked, dayEvents }) => {
  const { highlightedHoldPositionIds } = useMobileHoldChallenges();
  const cardRef = useRef(null);
  const highlighted = Boolean(event.hold_position_id) && highlightedHoldPositionIds.includes(event.hold_position_id);
  const isHold = event.type === 'Hold';
  const isConfirm = event.classification === 'confirm';
  const statusColor = STATUS_COLORS[event.classification] || 'var(--od-c-969696, #969696)';
  const statusLabel = STATUS_LABELS[event.classification] || '';
  const statusIcon = STATUS_ICONS[event.classification] || '/icons/hold.svg';
  const venueColor = event.venue_color || 'var(--od-c-969696, #969696)';

  // Determine margin based on classification change
  const classificationChanged = prevClassification && prevClassification !== event.classification;
  const marginBottom = classificationChanged ? '4px' : '2px';

  const isLive = ['announced', 'on_sale', 'pre_sale', 'artist_pre_sale'].includes(event.classification);
  const showTicketsSold = ['on_sale', 'pre_sale', 'artist_pre_sale', 'past'].includes(event.classification);
  const borderColor = isLive ? venueColor : 'var(--od-c-e9eaeb, #e9eaeb)';

  const cardStyle = {
    display: 'flex',
    alignItems: isHold ? 'center' : 'flex-start',
    padding: isHold ? '12px 16px' : '10px 12px',
    marginBottom,
    borderRadius: '8px',
    border: isHold ? 'none' : `1px solid ${borderColor}`,
    backgroundColor: 'var(--od-c-ffffff, #fff)',
    textDecoration: 'none',
    color: 'inherit',
    boxShadow: isHold ? 'none' : '0px 2px 0px 0px rgba(0,0,0,0.05)',
    ...(highlighted ? {
      boxShadow: '0 0 0 2px var(--sl-fg-warning)',
      backgroundColor: 'var(--sl-bg-warning-primary)'
    } : {})
  };

  useEffect(() => {
    if (highlighted) cardRef.current?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
  }, [highlighted]);

  // Vertical pill style matching Month view calendar
  const pillStyle = {
    width: '4px',
    minWidth: '4px',
    alignSelf: 'stretch',
    marginRight: '10px',
    borderRadius: '2px',
    backgroundColor: venueColor
  };

  const eventUrl = `/teams/${team.id}/confirms/${event.id}`;

  const challengeBolt = event.challenges?.length > 0 && (
    <i className={`fas fa-bolt${stacked ? '' : ' mr-2'} ${event.challenges.some((challenge) => challenge.overdue) ? 'hold-challenge-overdue' : 'hold-challenge-open'}`}
       title="Hold challenge in progress" />
  );

  return (
    <div ref={cardRef} style={cardStyle} className={`mobile-event-card position-relative${stacked ? ' flex-wrap' : ''}`}>
      {isHold ? (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '26px',
            height: '22px',
            border: `1px solid ${venueColor}`,
            borderRadius: '4px',
            marginRight: '10px',
            flexShrink: 0
          }}>
            <span style={{
              fontSize: '11px',
              fontWeight: '600',
              color: venueColor
            }}>
              H{event.position}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <a href={eventUrl} className={`stretched-link ${stacked ? 'd-inline' : 'd-block'}`} style={{
              fontSize: '15px',
              fontWeight: '400',
              color: 'var(--od-c-1a1a1a, #1a1a1a)',
              whiteSpace: stacked ? 'normal' : 'nowrap',
              overflow: stacked ? 'visible' : 'hidden',
              textOverflow: stacked ? 'clip' : 'ellipsis',
              wordBreak: stacked ? 'break-word' : 'normal',
              textDecoration: 'none'
            }}>
              {event.title}
            </a>
            {stacked && challengeBolt && <>{' '}{challengeBolt}</>}
          </div>
          {!stacked && challengeBolt}
          <MobileHoldChallengeButton hold={event} dayEvents={dayEvents} stacked={stacked} />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            border: '1px solid var(--od-c-9ca3af, #9CA3AF)',
            borderRadius: '50%',
            marginLeft: '12px',
            flexShrink: 0
          }}>
            <span style={{
              fontSize: '10px',
              fontWeight: '500',
              color: 'var(--od-c-9ca3af, #9CA3AF)'
            }}>
              H
            </span>
          </div>
        </>
      ) : (
        // Confirmed/Announced/On-sale event - full card with vertical pill
        <>
          {/* Vertical pill matching Month view calendar */}
          <div style={pillStyle}></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <a href={eventUrl} className="stretched-link" style={{
              fontSize: '15px',
              fontWeight: '600',
              color: 'var(--od-c-1a1a1a, #1a1a1a)',
              marginBottom: '2px',
              whiteSpace: stacked ? 'normal' : 'nowrap',
              overflow: stacked ? 'visible' : 'hidden',
              textOverflow: stacked ? 'clip' : 'ellipsis',
              wordBreak: stacked ? 'break-word' : 'normal',
              display: 'block',
              textDecoration: 'none'
            }}>
              {event.title}
            </a>
            <div style={{
              fontSize: '13px',
              color: 'var(--od-c-717680, #717680)',
              marginBottom: (isConfirm || showTicketsSold) && (event.promoter || event.tickets_sold) ? '4px' : '0'
            }}>
              {event.human_start_time || formatTime(event.start_time)}
            </div>
            {(isConfirm || showTicketsSold) && (event.promoter || event.tickets_sold) && (
              <div style={{
                fontSize: '12px',
                color: 'var(--od-c-717680, #717680)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {event.promoter && (
                  <span>Presenter: <strong>{event.promoter}</strong></span>
                )}
                {event.tickets_sold && (
                  <span>Tickets sold: <strong>{event.tickets_sold}</strong></span>
                )}
              </div>
            )}
            {stacked && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '4px',
                color: 'var(--od-c-717680, #717680)',
                fontSize: '12px'
              }}>
                {statusLabel}
                <img src={statusIcon} alt="" style={{ width: '14px', height: '14px' }} />
              </div>
            )}
          </div>
          {!stacked && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: '12px',
              color: 'var(--od-c-717680, #717680)',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}>
              {statusLabel}
              <img src={statusIcon} alt="" style={{ width: '18px', height: '18px' }} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

EventCard.propTypes = {
  event: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  prevClassification: PropTypes.string,
  stacked: PropTypes.bool,
  dayEvents: PropTypes.array
};

EventCard.defaultProps = {
  stacked: false,
  dayEvents: []
};

const DateGroup = ({ date, events, team }) => {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--od-c-1a1a1a, #1a1a1a)',
        marginBottom: '12px',
        paddingLeft: '4px'
      }}>
        {formatDate(date)}
      </h3>
      {events.map((event, index) => (
        <EventCard
          key={event.id || index}
          event={event}
          team={team}
          prevClassification={index > 0 ? events[index - 1].classification : null}
          dayEvents={events}
        />
      ))}
    </div>
  );
};

DateGroup.propTypes = {
  date: PropTypes.string.isRequired,
  events: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired
};

const MobileEventList = ({
  team,
  dataSource,
  filters,
  searchTerm,
  selectedRooms,
  refreshSignal
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 50;
  const latestRequestId = useRef(0);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const selectedRoomIds = useMemo(
    () => (selectedRooms || []).map(r => r.value),
    [selectedRooms]
  );
  const selectedRoomIdsKey = selectedRoomIds.join(',');

  const fetchEvents = useCallback(async (start, append = false, length = pageSize) => {
    const requestId = ++latestRequestId.current;
    if (start === 0) {
      setLoading(true);
    }

    try {
      const roomIds = filters.room_ids || selectedRoomIds;

      const params = {
        ...filters,
        room_ids: roomIds,
        start,
        length,
        'search[value]': searchTerm || '',
        'order[0][column]': 1,
        'order[0][dir]': 'asc',
        mobile: true
      };

      // Convert datatable URL to mobile_datatable URL
      const mobileUrl = dataSource.replace('/datatable', '/mobile_datatable');

      const response = await axios.get(mobileUrl, {
        params,
        headers: {
          'Accept': 'application/json'
        }
      });
      if (requestId !== latestRequestId.current) return;

      const newEvents = response.data.data || [];

      if (append) {
        setEvents(prev => [...prev, ...newEvents]);
      } else {
        setEvents(newEvents);
      }

      setHasMore(newEvents.length === length);
    } catch (error) {
      if (requestId !== latestRequestId.current) return;
      console.error('Error fetching mobile events:', error);
      toastError('Failed to load events. Please try again.');
      setHasMore(false);
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, filtersKey, searchTerm, pageSize, selectedRoomIdsKey]);

  useEffect(() => {
    const roomIds = filters.room_ids || selectedRoomIds;
    if (roomIds.length === 0) {
      latestRequestId.current++;
      setLoading(false);
      return;
    }

    setEvents([]);
    fetchEvents(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEvents]);

  const loadedCount = useRef(0);
  loadedCount.current = events.length;

  useEffect(() => {
    if (!refreshSignal) return;
    fetchEvents(0, false, Math.max(loadedCount.current, pageSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Load more function for react-infinite-scroll-component
  const loadMore = () => {
    fetchEvents(events.length, true);
  };

  const groupedEvents = groupEventsByDate(events);
  // Sort by ISO date keys (YYYY-MM-DD format sorts correctly as strings)
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => a.localeCompare(b));

  if (loading && events.length === 0) {
    return (
      <div style={{
        padding: '40px 16px',
        textAlign: 'center',
        color: 'var(--od-c-717680, #717680)'
      }}>
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <div style={{ marginTop: '12px' }}>Loading events...</div>
      </div>
    );
  }

  if (!loading && events.length === 0) {
    return (
      <div style={{
        padding: '40px 16px',
        textAlign: 'center',
        color: 'var(--od-c-717680, #717680)'
      }}>
        <div style={{ fontSize: '16px', marginBottom: '8px' }}>No events found</div>
        <div style={{ fontSize: '14px' }}>Try adjusting your filters or search term</div>
      </div>
    );
  }

  return (
    <InfiniteScroll
      dataLength={events.length}
      next={loadMore}
      hasMore={hasMore}
      loader={
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: 'var(--od-c-717680, #717680)'
        }}>
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="sr-only">Loading more...</span>
          </div>
        </div>
      }
      style={{ padding: '0 4px' }}
      className="mobile-event-list"
    >
      {sortedDates.map(dateKey => (
        <DateGroup
          key={dateKey}
          date={groupedEvents[dateKey].displayDate}
          events={groupedEvents[dateKey].events}
          team={team}
        />
      ))}
    </InfiniteScroll>
  );
};

MobileEventList.propTypes = {
  team: PropTypes.object.isRequired,
  dataSource: PropTypes.string.isRequired,
  filters: PropTypes.object,
  searchTerm: PropTypes.string,
  selectedRooms: PropTypes.array,
  refreshSignal: PropTypes.number
};

MobileEventList.defaultProps = {
  filters: {},
  searchTerm: '',
  selectedRooms: [],
  refreshSignal: 0
};

export { EventCard, DateGroup, STATUS_COLORS, STATUS_LABELS, STATUS_ICONS, formatDate, formatTime };
export default MobileEventList;
